import "../index.css";
import Footer from "../components/Footer";
import { PageNav } from "../components/PageNav";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import ApiCall from "../auth/ApiCall";
import { useAdaptivePolling } from "../hooks/useAdaptivePolling";
import MyChart from "../components/MyChart";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { AccessVerification, ProAccessCheck } from "../auth/UserProfile";
import {
  labelForGameOddsKey,
  getSideGroupInfo,
  getOverUnderPriceOptions,
  getPlayerOverUnderPriceOptions,
  getSpreadSideInfo,
  getTeamOverUnderPriceOptions,
} from "./gamePropsConfig";

interface Game {
  id: number;
  sport: string;
  sport_id: number;
  home_team: string;
  away_team: string;
  home_team_id: number;
  away_team_id: number;
  start_time: string | number;
  home_score: number;
  away_score: number;
  score_details: Record<string, Record<string, unknown>>;
  game_status: Record<string, unknown>;
  odds: Record<string, Record<string, unknown>>;
  watching: boolean;
}

// Fallback palette for bookmakers without a stored color code
const PALETTE = [
  "#4e79a7", "#f28e2c", "#e15759", "#76b7b2", "#59a14f",
  "#edc949", "#af7aa1", "#ff9da7", "#9c755f", "#bab0ab",
];

function resolveBookColor(
  bookColors: Record<string, string>,
  book: string,
  index: number,
): string {
  return bookColors[book] || PALETTE[index % PALETTE.length];
}

// Player Odds dropdown labels that replace the default title-cased stat name
// ("player_any_assists_price" -> "Player Any Assists Price"). Keyed by the stat
// base (the player key minus ":{player_id}" and any _over/_under suffix); exact
// match only, so every other stat keeps its generated label.
const PLAYER_STAT_DROPDOWN_LABELS: Record<string, string> = {
  most_points_player_price: "Most Points",
  player_any_assists_price: "Any Assists",
  player_any_blocks_price: "Any Blocks",
  player_any_blocks_steals_price: "Any Blocks + Steals",
  player_any_rebounds_price: "Any Rebounds",
  player_any_steals_price: "Any Steals",
  player_any_three_pointers_made_price: "Any Three Pointers",
  player_any_turnovers_price: "Any Turnovers",
  player_assists: "Assists",
  player_blocks: "Blocks",
  player_blocks_steals: "Blocks + Steals",
  player_double_double_price: "Double Double",
  player_dunks: "Dunks",
  player_fantasy_score: "Fantasy Score",
  player_first_basket_price: "First Basket",
  player_first_half_fantasy_score: "1st Half Fantasy Score",
  player_first_half_points: "1st Half Points",
  player_first_half_points_rebounds_assists:
    "1st Half Points + Rebounds + Assists",
  player_first_quarter_any_assists_price: "1st Quarter Any Assists",
  player_first_quarter_any_points_price: "1st Quarter Any Points",
  player_first_quarter_any_rebounds_price: "1st Quarter Any Rebounds",
  player_first_quarter_assists: "1st Quarter Assists",
  player_first_quarter_points: "1st Quarter Points",
  player_first_quarter_rebounds: "1st Quarter Rebounds",
  player_points: "Points",
  player_points_assists: "Points + Assists",
  player_points_rebounds: "Points + Rebounds",
  player_points_rebounds_assists: "Points + Rebounds + Assists",
  player_rebounds: "Rebounds",
  player_rebounds_assists: "Rebounds + Assists",
  player_steals: "Steals",
  player_three_pointers_made: "Three Pointers Made",
  player_triple_double_price: "Triple Double",
  player_turnovers: "Turnovers",
};

// Per-sport overrides of the labels above, keyed by Sport id: MLB's player_points
// is a batter's Runs, not basketball Points.
const PLAYER_STAT_DROPDOWN_LABELS_BY_SPORT: Record<number, Record<string, string>> = {
  4: { player_points: "Runs" },
};


function isPriceKey(key: string): boolean {
  return key.includes("_price") || key.includes("_outcome");
}

function msFromStartTime(raw: string | number): number {
  if (typeof raw === "number") return raw < 1e10 ? raw * 1000 : raw;
  const asNum = Number(raw);
  return !isNaN(asNum)
    ? asNum < 1e10 ? asNum * 1000 : asNum
    : Date.parse(String(raw));
}

export default function PropDetails() {
  const { gameId } = useParams<{ gameId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [currentGameId, setCurrentGameId] = useState<number>(
    gameId ? parseInt(gameId, 10) : -1,
  );
  const [propData, setPropData] = useState<Record<string, unknown>>();
  // Tracks which game ID the current propData belongs to — prevents stale data from a
  // previous game from appearing in the dropdown while the new game's fetch is in-flight.
  const [propDataGameId, setPropDataGameId] = useState<number | null>(null);
  const [games, setGames] = useState<Game[]>([]);
  const [currentGame, setCurrentGame] = useState<Game | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [everHadKeys, setEverHadKeys] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [menuVisible, setMenuVisible] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string>(
    searchParams.get("chart") || "",
  );
  const propType = searchParams.get("type") as "game" | "player" | null;

  const initialGameSetRef = useRef(!!gameId);
  const prevGameIdRef = useRef<string | undefined>(gameId);
  const currentGameIdRef = useRef(currentGameId);
  // Set during menu navigation to suppress the URL sync effect until the URL catches up
  const pendingNavGameIdRef = useRef<number | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const redirectPath = AccessVerification() || ProAccessCheck();
    if (redirectPath) navigate(redirectPath);
  }, [navigate]);

  // Keep ref in sync so in-flight fetches can detect stale responses
  useEffect(() => {
    currentGameIdRef.current = currentGameId;
  }, [currentGameId]);

  // Sync state when URL gameId param changes (direct URL nav; menu nav is handled in navigateToGame).
  // When pendingNavGameIdRef is set we're mid menu-navigation — React Router (even with
  // BrowserRouter) can render the route optimistically with the new gameId and then revert
  // it while the transition is still in-flight. Suppress ALL URL sync firings while pending;
  // the flag is cleared in fetchPropData's finally block once the new game's data lands.
  useEffect(() => {
    if (!gameId) return;
    if (pendingNavGameIdRef.current !== null) return;
    const urlGameId = parseInt(gameId, 10);
    if (gameId !== prevGameIdRef.current) {
      prevGameIdRef.current = gameId;
      setPropData(undefined);
      setPropDataGameId(null);
      setIsLoading(true);
      setEverHadKeys(false);
      setCurrentGameId(urlGameId);
      setCurrentGame(null);
    }
  }, [gameId]);

  // Sync selectedKey when the URL's "chart" param changes externally (e.g.
  // browser back/forward) — handleKeyChange and fetchPropData's own key
  // resolution already keep selectedKey and the URL in sync when this
  // component causes the change, so this effect is a no-op then and only
  // does something on external navigation.
  useEffect(() => {
    const urlKey = searchParams.get("chart") || "";
    if (urlKey !== selectedKey) {
      setSelectedKey(urlKey);
      setPropData(undefined);
      setIsLoading(true);
    }
  }, [searchParams, selectedKey]);

  const lastSuccessRef = useRef<number | null>(null);
  // Serialized copy of the payload currently in propData. Polling returns the
  // same data most of the time — skipping setPropData when nothing changed
  // avoids re-rendering (and re-allocating) the whole chart every 5s.
  const lastPayloadRef = useRef<string | null>(null);
  const propDataRef = useRef(propData);
  useEffect(() => {
    propDataRef.current = propData;
    if (!propData) lastPayloadRef.current = null;
  }, [propData]);

  const fetchPropData = useCallback(async (signal: AbortSignal) => {
    if (currentGameId < 0) return;
    const fetchedForId = currentGameId;

    const resolveScoped = (availKeys: { game_odds: string[]; player_odds: string[] }) => {
      const raw = availKeys?.game_odds || [];
      // Note: this scopes which keys are considered "valid" for default-picking
      // and the resolvedKey validity check below — it does NOT control what's
      // listed in the main dropdown (gameOddsOptions handles that separately,
      // and still excludes these). Over/Under price keys (e.g.
      // "away_over_total_price") and the "_under_total" point key must stay in
      // scope here since they're selectable via the Over/Under Value/Price
      // dropdown — excluding them made a selected price key look "invalid" and
      // get silently reset back to the point key.
      return propType === "game"
        ? raw
        : propType === "player"
          ? (availKeys?.player_odds || [])
          : [...raw, ...(availKeys?.player_odds || [])];
    };

    const applyToData = (data: Record<string, unknown>) => {
      if (currentGameIdRef.current !== fetchedForId) return;
      const serialized = JSON.stringify(data);
      if (!propDataRef.current || serialized !== lastPayloadRef.current) {
        lastPayloadRef.current = serialized;
        setPropData(data);
      }
      setPropDataGameId(fetchedForId);
      setCurrentGame((prev) => {
        if (prev) return prev;
        const gd = data["game_data"] as Record<string, unknown>;
        if (!gd) return prev;
        return {
          id: currentGameId,
          sport: (gd["sport"] as string) || "",
          sport_id: (gd["sport_id"] as number) || 0,
          home_team: (gd["home_team"] as string) || "",
          away_team: (gd["away_team"] as string) || "",
          home_team_id: (gd["home_team_id"] as number) || 0,
          away_team_id: (gd["away_team_id"] as number) || 0,
          start_time: (gd["start_time"] as string | number) || "",
          home_score: (gd["home_score"] as number) || 0,
          away_score: (gd["away_score"] as number) || 0,
          score_details: (gd["score_details"] as Record<string, Record<string, unknown>>) || {},
          game_status: (gd["game_status"] as Record<string, unknown>) || {},
          odds: {},
          watching: false,
        };
      });
      lastSuccessRef.current = Date.now();
      setError(null);
    };

    try {
      const keyParam = selectedKey ? `?key=${encodeURIComponent(selectedKey)}` : "";
      const response = await ApiCall.makeAuthenticatedRequest(
        `/api/prop_data/${currentGameId}/${keyParam}`,
        { signal },
      );
      if (signal.aborted) return;
      if ("error" in response) throw new Error("Network error");
      const data = response as Record<string, unknown>;

      const availKeys = data["available_keys"] as { game_odds: string[]; player_odds: string[] };
      const scopedKeys = resolveScoped(availKeys);

      if (scopedKeys.length > 0) setEverHadKeys(true);

      // The available_keys list isn't always a complete/accurate inventory —
      // a key can be missing from it even when the backend just returned real
      // chart data for that exact key (seen with away_spread_point). Trust
      // actual data over that metadata: only treat a selected key as "invalid"
      // needing correction when there's truly nothing to chart for it.
      const hasChartData =
        ((data["pregame_props"] as unknown[]) || []).length > 0 ||
        ((data["live_props"] as unknown[]) || []).length > 0;

      // Preferred defaults when no key is selected or the current key doesn't exist in
      // the new game's available keys (e.g. after switching sports). These are roughly
      // sport-agnostic — every sport tracks game total and spread.
      const PREFERRED_DEFAULTS = ["over_total_point", "away_spread_point", "away_outcome"];
      const pickDefault = (keys: string[]) =>
        (propType !== "player" && PREFERRED_DEFAULTS.find((k) => keys.includes(k))) || keys[0];

      // Determine the correct key to display
      let resolvedKey = selectedKey;
      if (!resolvedKey && scopedKeys.length > 0) {
        resolvedKey = pickDefault(scopedKeys);
      } else if (resolvedKey && scopedKeys.length > 0 && !scopedKeys.includes(resolvedKey) && !hasChartData) {
        const normalize = (k: string) =>
          k.replace(/_(over|under)_(price|point)(:|$)/, "$3")
           .replace(/_(over|under)(:|$)/, "$2")
           .replace(/_(price|point)(:|$)/, "$2");
        const normalizedResolved = normalize(resolvedKey);
        resolvedKey =
          scopedKeys.find((k) =>
            k.startsWith(resolvedKey) ||
            k.includes(resolvedKey) ||
            normalize(k) === normalizedResolved,
          ) ||
          pickDefault(scopedKeys);
      }

      if (resolvedKey && resolvedKey !== selectedKey) {
        // Key needed resolving — update URL/state and immediately fetch chart data
        // without waiting for a React re-render cycle (avoids a full extra round-trip).
        setSelectedKey(resolvedKey);
        setSearchParams(
          propType ? { chart: resolvedKey, type: propType } : { chart: resolvedKey },
          { replace: true },
        );
        const response2 = await ApiCall.makeAuthenticatedRequest(
          `/api/prop_data/${currentGameId}/?key=${encodeURIComponent(resolvedKey)}`,
          { signal },
        );
        if (signal.aborted) return;
        if (!("error" in response2)) {
          applyToData(response2 as Record<string, unknown>);
        }
        return;
      }

      // Key was already correct — use this response directly
      applyToData(data);
    } catch (err) {
      const neverLoaded = lastSuccessRef.current === null;
      const stale = lastSuccessRef.current !== null && Date.now() - lastSuccessRef.current >= 60_000;
      if (neverLoaded || stale) {
        setError(
          "Failed to load prop data: " +
            (err instanceof Error ? err.message : "Unknown error"),
        );
      }
    } finally {
      if (!signal.aborted) {
        setIsLoading(false);
        // Navigation confirmed: new game's fetch completed — safe to allow URL sync again.
        if (pendingNavGameIdRef.current === fetchedForId) {
          prevGameIdRef.current = String(fetchedForId);
          pendingNavGameIdRef.current = null;
        }
      }
    }
  }, [currentGameId, selectedKey, propType]);

  useAdaptivePolling(fetchPropData);

  const fetchGames = useCallback(async (signal: AbortSignal) => {
    try {
      const response = await ApiCall.makeAuthenticatedRequest(
        "/api/active_games_data/",
        { signal },
      );
      if (signal.aborted) return;
      if ("error" in response) return;
      const data = response as Record<string, unknown>;
      const allGames = data["events"] as Game[];
      setGames(allGames);
      if (!initialGameSetRef.current) {
        initialGameSetRef.current = true;
        const urlId = gameId ? parseInt(gameId, 10) : -1;
        const game = allGames.find((g) => g.id === urlId) || allGames[0];
        if (game) {
          setCurrentGame(game);
          setCurrentGameId(game.id);
        }
      } else if (currentGameId > 0) {
        setCurrentGame((prev) => {
          if (prev) return prev;
          return allGames.find((g) => g.id === currentGameId) || prev;
        });
      }
    } catch {
      // silent
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId]);

  useAdaptivePolling(fetchGames);

  const availableKeys = useMemo(() => {
    const empty = { game_odds: [] as string[], player_odds: [] as string[] };
    // Don't expose keys from a previous game's data while the new game is loading.
    if (!propData || propDataGameId !== currentGameId) return empty;
    return (
      (propData["available_keys"] as {
        game_odds: string[];
        player_odds: string[];
      }) || empty
    );
  }, [propData, propDataGameId, currentGameId]);

  // One deduplicated option per compound group (e.g. both away/home first-half
  // over keys collapse to a single "First Half Team Over/Under" entry).
  const gameOddsOptions = useMemo(() => {
    const seen = new Map<string, string>(); // label → representative key
    for (const key of availableKeys.game_odds) {
      // TODO: Over/Under side selector needs another look before re-enabling.
      // if (key.includes("_over_total") && !key.endsWith("_point")) continue;
      // if (key.includes("_under_total") && !key.endsWith("_point")) continue;
      if (key.includes("_under_total")) continue;
      if (key.includes("_over_total") && !key.endsWith("_point")) continue;
      // Spread price keys are only reachable via the Away/Home side dropdown,
      // never as their own main-dropdown entry.
      if (key.includes("_spread") && key.endsWith("_price")) continue;
      const label = labelForGameOddsKey(key);
      const existing = seen.get(label);
      if (!existing) {
        seen.set(label, key);
      } else {
        // Default multi-sided groups (Yes/No, Even/Odd, Away/Home, Double
        // Chance) to their first canonical side as the dropdown's representative key.
        const newInfo = getSideGroupInfo(key);
        const existingInfo = getSideGroupInfo(existing);
        if (newInfo?.currentIndex === 0 && existingInfo && existingInfo.currentIndex !== 0) {
          seen.set(label, key);
        } else if (
          getTeamOverUnderPriceOptions(key)?.currentIndex === 0 &&
          getTeamOverUnderPriceOptions(existing)?.currentIndex !== 0
        ) {
          // Team Over/Under: default to the Away side, matching the side
          // dropdown's first entry.
          seen.set(label, key);
        }
        // TODO: Over/Under side selector needs another look before re-enabling.
        // else if (!newInfo && !existingInfo) {
        //   // Groups with no recognized side pairing (e.g. 4-way team
        //   // Over/Under) still default to the "Over" side.
        //   if (!key.includes("_under_total") && existing.includes("_under_total")) {
        //     seen.set(label, key);
        //   }
        // }
      }
    }
    return [...seen.entries()]
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [availableKeys.game_odds]);

  // Info about the other sides of the currently selected key, if it belongs
  // to a recognized multi-sided compound group (Yes/No, Even/Odd, Away/Home,
  // or the 3-way Double Chance set).
  const sideGroupInfo = useMemo(
    () =>
      selectedKey
        ? getSideGroupInfo(selectedKey, (k) => availableKeys.game_odds.includes(k))
        : null,
    [selectedKey, availableKeys.game_odds],
  );
  // Only offer the side dropdown once every side's key is actually present
  // in this game's data.
  const sideGroupAvailable =
    !!sideGroupInfo && sideGroupInfo.keys.every((k) => availableKeys.game_odds.includes(k));

  // For spread markets, the richer 4-option Away/Home/Away Price/Home Price
  // dropdown — preferred over the generic 2-option Away/Home selector above
  // (which also matches spread's Away/Home rowLabels) whenever price data is
  // fully available; otherwise the generic point-only selector still applies.
  const spreadSideInfo = useMemo(
    () => (selectedKey ? getSpreadSideInfo(selectedKey) : null),
    [selectedKey],
  );
  const spreadSideAvailable =
    !!spreadSideInfo && spreadSideInfo.keys.every((k) => availableKeys.game_odds.includes(k));

  // For team-specific Over/Under totals (away_over_total, home_under_total,
  // etc.), the 6-option Away/Home Over/Under / Away Over/Under Price / Home
  // Over/Under Price dropdown — also preferred over the generic selector,
  // and mutually exclusive with spread (base patterns never overlap).
  const teamOverUnderInfo = useMemo(
    () => (selectedKey ? getTeamOverUnderPriceOptions(selectedKey) : null),
    [selectedKey],
  );
  const teamOverUnderAvailable =
    !!teamOverUnderInfo && teamOverUnderInfo.keys.every((k) => availableKeys.game_odds.includes(k));

  const activeSideInfo = spreadSideAvailable
    ? spreadSideInfo
    : teamOverUnderAvailable
      ? teamOverUnderInfo
      : sideGroupInfo;
  const activeSideAvailable = spreadSideAvailable || teamOverUnderAvailable || sideGroupAvailable;

  // For Over/Under total markets (game or player), info needed for the
  // "Over/Under Value" / "Over Price" / "Under Price" dropdown. Player keys
  // are colon-separated ("<stat>_over_point:<playerId>") and use a different
  // suffix grammar than game keys, hence the two helpers.
  const overUnderPriceInfo = useMemo(
    () =>
      selectedKey
        ? (getOverUnderPriceOptions(selectedKey) ?? getPlayerOverUnderPriceOptions(selectedKey))
        : null,
    [selectedKey],
  );
  const overUnderPriceAvailKeys = selectedKey?.includes(":")
    ? availableKeys.player_odds
    : availableKeys.game_odds;
  // Under data doesn't always exist (some books/stats only offer Over) — only
  // require Over price for the dropdown to appear, and include Under Price as
  // an option only when it's actually there.
  const overUnderHasOverPrice =
    !!overUnderPriceInfo && overUnderPriceAvailKeys.includes(overUnderPriceInfo.overPriceKey);
  const overUnderHasUnderPrice =
    !!overUnderPriceInfo && overUnderPriceAvailKeys.includes(overUnderPriceInfo.underPriceKey);
  const overUnderPriceAvailable = overUnderHasOverPrice;
  const overUnderValueLabel = overUnderHasUnderPrice ? "Over/Under Value" : "Over Value";
  const overUnderPriceValue: "value" | "overPrice" | "underPrice" =
    overUnderPriceInfo?.overPriceKey === selectedKey
      ? "overPrice"
      : overUnderPriceInfo?.underPriceKey === selectedKey
        ? "underPrice"
        : "value";

  // The main dropdown only lists one representative key per multi-sided group
  // (the first canonical side) — when another side is active, show that
  // representative as selected there while the side dropdown reflects the
  // actual side being charted. Likewise, Over/Under price keys aren't listed
  // as their own option — show the line's point key as selected instead.
  const mainDropdownValue =
    activeSideAvailable && activeSideInfo!.currentIndex !== 0
      ? activeSideInfo!.keys[0]
      : overUnderPriceAvailable && overUnderPriceValue !== "value"
        ? overUnderPriceInfo!.pointKey
        : selectedKey;

  const bookColors = useMemo(() => {
    if (!propData) return {};
    const bm = (propData["bookmakers"] as Record<string, string>) || {};
    const books = (propData["books"] as string[]) || [];
    const result: Record<string, string> = {};
    books.forEach((book, i) => {
      result[book] = resolveBookColor(bm, book, i);
    });
    return result;
  }, [propData]);

  const playerMetadata = useMemo(() => {
    if (!propData) return {} as Record<string, string>;
    // Backend sends player_names as a flat { player_id: display_name } map.
    // Fall back to player_metadata (legacy field) if player_names is absent.
    const flat = propData["player_names"];
    if (flat && typeof flat === "object" && !Array.isArray(flat)) {
      return flat as Record<string, string>;
    }
    const legacy = propData["player_metadata"];
    const result: Record<string, string> = {};
    if (!legacy) return result;
    if (Array.isArray(legacy)) {
      (legacy as Record<string, unknown>[]).forEach((entry) => {
        const id = String(entry["player_id"] ?? "");
        const name = String(entry["display_name"] ?? entry["name"] ?? "");
        if (id && name) result[id] = name;
      });
    } else if (typeof legacy === "object") {
      Object.entries(legacy as Record<string, unknown>).forEach(([id, entry]) => {
        if (typeof entry === "string") {
          if (entry) result[id] = entry;
        } else if (entry && typeof entry === "object") {
          const obj = entry as Record<string, unknown>;
          const name = String(obj["display_name"] ?? obj["name"] ?? "");
          if (name) result[id] = name;
        }
      });
    }
    return result;
  }, [propData]);

  const sportId = currentGame?.sport_id ?? 0;

  // One option per (stat type, player) pair — prefer the _over_point key as representative
  const playerOptions = useMemo(() => {
    const seen = new Map<string, string>(); // "statBase:playerId" → representative key
    for (const key of availableKeys.player_odds) {
      const colonIdx = key.indexOf(":");
      if (colonIdx === -1) continue;
      const section = key.substring(0, colonIdx);
      // The No side of a yes/no prop ("{stem}_no_price") isn't charted - the Yes
      // side ("{stem}_price") is the one the price-only player rules track.
      if (section.endsWith("_no_price")) continue;
      const playerId = key.substring(colonIdx + 1);
      const base = section.replace(
        /_over_point$|_under_point$|_over_price$|_under_price$|_over$|_under$/,
        "",
      );
      const groupKey = `${base}:${playerId}`;
      if (!seen.has(groupKey) || section.endsWith("_over_point")) {
        seen.set(groupKey, key);
      }
    }
    return [...seen.entries()].map(([groupKey, repKey]) => {
      const ci = groupKey.indexOf(":");
      const base = ci !== -1 ? groupKey.substring(0, ci) : groupKey;
      const playerId = ci !== -1 ? groupKey.substring(ci + 1) : "";
      const statLabel =
        PLAYER_STAT_DROPDOWN_LABELS_BY_SPORT[sportId]?.[base] ??
        PLAYER_STAT_DROPDOWN_LABELS[base] ??
        base.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
      const playerName = playerMetadata[playerId] || playerId;
      return { label: `${statLabel} - ${playerName}`, value: repKey };
    }).sort((a, b) => a.label.localeCompare(b.label));
  }, [availableKeys.player_odds, playerMetadata, sportId]);

  const handleKeyChange = (newKey: string) => {
    setSelectedKey(newKey);
    setSearchParams(propType ? { chart: newKey, type: propType } : { chart: newKey });
    setPropData(undefined);
    setIsLoading(true);
  };

  const navigateToGame = (index: number) => {
    setMenuVisible(false);
    const game = games[index];
    if (game && game.id !== currentGameId) {
      // The in-flight prop fetch is aborted when currentGameId changes below (the
      // polling hook restarts), and applyToData ignores responses for a stale game id.
      // Reset state so the header and dropdown reflect the new game right away.
      setCurrentGame(game);
      setPropData(undefined);
      setPropDataGameId(null);
      setIsLoading(true);
      setEverHadKeys(false);
      setSelectedKey("");
      setCurrentGameId(game.id);
      // Signal the URL-sync effect to ignore stale firings until the URL catches up.
      pendingNavGameIdRef.current = game.id;
      // Don't carry the old game's chart key — let the new game pick its default.
      const params = new URLSearchParams();
      if (propType) params.set("type", propType);
      const qs = params.toString() ? `?${params.toString()}` : "";
      navigate(`/game/${game.id}/props/chart${qs}`);
    }
  };

  const getIconAndColor = (sportId: number): string => {
    const icons: Record<number, string> = {
      1: "🏀 ", 2: "🏀 ", 3: "🏒 ", 4: "⚾️ ",
      5: "🏈 ", 6: "🏈 ", 7: "⚽️ ", 8: "⚽️ ",
    };
    return icons[sportId] || "";
  };

  const gameNotStarted = currentGame
    ? msFromStartTime(currentGame.start_time) > Date.now()
    : false;

  const oddsKey = gameNotStarted ? "pregame_props" : "live_props";

  if (isLoading && !propData) {
    return (
      <div>
        <PageNav />
        <div className="main_div">
          <div className="center_me" style={{ padding: 40 }}>
            <p className="font_family_base">Loading...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error && !propData) {
    return (
      <div>
        <PageNav />
        <div className="main_div">
          <div className="center_me" style={{ padding: 40 }}>
            <span>⛔️</span> {error}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const chartData = propData
    ? (propData[oddsKey] as Array<
        Record<string, string | number | null | undefined>
      >)
    : undefined;

  const gameData = propData?.["game_data"] as Record<string, unknown>;

  return (
    <div>
      <PageNav />
      <div className="main_div">
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "0 10px",
            width: "100%",
          }}
        >
          {/* Game header / switcher */}
          <div
            className="center_me"
            style={{
              display: "flex",
              gap: 10,
              marginBottom: 10,
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={() => setMenuVisible(true)}
              className="font_family_base"
              style={{
                padding: "15px 20px",
                color: "#fff",
                border: "none",
                cursor: "pointer",
                flex: "0 0 auto",
                background: "transparent",
              }}
            >
              <span
                className="font_family_base"
                style={{
                  fontSize: 16,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                {getIconAndColor(currentGame?.sport_id || 0)}
                {currentGame?.away_team ?? "—"} vs{" "}
                {currentGame?.home_team ?? "—"} ▼
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: "bold",
                    padding: "2px 6px",
                    borderRadius: 4,
                    backgroundColor: gameNotStarted ? "#555" : "#c0392b",
                    color: "#fff",
                  }}
                >
                  {gameNotStarted ? "Pregame" : "Live"}
                </span>
              </span>
            </button>
          </div>

          {/* Score */}
          {currentGame && (
            <div className="center_me" style={{ padding: "4px 0 12px" }}>
              <p
                className="font_family_base"
                style={{ fontSize: 16, color: "#4CAF50" }}
              >
                {currentGame.away_team}: {currentGame.away_score} –{" "}
                {currentGame.home_team}: {currentGame.home_score}
              </p>
            </div>
          )}

          {/* Prop key dropdown */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: 10,
              marginBottom: 20,
              padding: "0 10px",
            }}
          >
            <select
              value={mainDropdownValue}
              onChange={(e) => handleKeyChange(e.target.value)}
              className="font_family_base"
              style={{
                padding: "10px 14px",
                fontSize: 15,
                borderRadius: 8,
                border: "1px solid #444",
                backgroundColor: "#181818",
                color: "#fff",
                cursor: "pointer",
                minWidth: 280,
                maxWidth: "100%",
              }}
            >
              {gameOddsOptions.length === 0 &&
                playerOptions.length === 0 && (
                  <option value="">
                    {everHadKeys && !isLoading ? "No prop data available" : "Loading prop data…"}
                  </option>
                )}
              {propType !== "player" && gameOddsOptions.length > 0 && (
                <optgroup label="Game Odds">
                  {gameOddsOptions.map(({ label, value }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </optgroup>
              )}
              {propType !== "game" && playerOptions.length > 0 && (
                <optgroup label="Player Odds">
                  {playerOptions.map(({ label, value }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            {activeSideAvailable && (
              <select
                value={activeSideInfo!.currentIndex}
                onChange={(e) => {
                  const index = Number(e.target.value);
                  if (index !== activeSideInfo!.currentIndex) {
                    handleKeyChange(activeSideInfo!.keys[index]);
                  }
                }}
                className="font_family_base"
                style={{
                  padding: "10px 14px",
                  fontSize: 15,
                  borderRadius: 8,
                  border: "1px solid #444",
                  backgroundColor: "#181818",
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                {activeSideInfo!.labels.map((label, index) => (
                  <option key={label} value={index}>
                    {label}
                  </option>
                ))}
              </select>
            )}
            {overUnderPriceAvailable && (
              <select
                value={overUnderPriceValue}
                onChange={(e) => {
                  const v = e.target.value as "value" | "overPrice" | "underPrice";
                  if (v === overUnderPriceValue) return;
                  const target =
                    v === "overPrice"
                      ? overUnderPriceInfo!.overPriceKey
                      : v === "underPrice"
                        ? overUnderPriceInfo!.underPriceKey
                        : overUnderPriceInfo!.pointKey;
                  handleKeyChange(target);
                }}
                className="font_family_base"
                style={{
                  padding: "10px 14px",
                  fontSize: 15,
                  borderRadius: 8,
                  border: "1px solid #444",
                  backgroundColor: "#181818",
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                <option value="value">{overUnderValueLabel}</option>
                <option value="overPrice">Over Price</option>
                {overUnderHasUnderPrice && (
                  <option value="underPrice">Under Price</option>
                )}
              </select>
            )}
          </div>
        </div>

        {/* Chart */}
        {propData && !isLoading && (
          <MyChart
            data={chartData}
            books={(propData["books"] as string[]) || []}
            bookColors={bookColors}
            isLiveGame={!gameNotStarted}
            startTime={(gameData?.["start_time"] as number) || 0}
            isMoneyline={isPriceKey(selectedKey)}
            vertical_markers={
              (gameData?.["vertical_markers"] as Record<string, number>) || {}
            }
            game_part_name={
              (gameData?.["sport_part_name"] as string) || ""
            }
            showPlusSign={isPriceKey(selectedKey)}
          />
        )}

        {/* Side menu overlay */}
        {menuVisible && (
          <>
            <div
              onClick={() => setMenuVisible(false)}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                zIndex: 999,
              }}
            />
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                bottom: 0,
                width: "min(80%, 300px)",
                backgroundColor: "#000",
                borderRight: "1px solid #333",
                zIndex: 1000,
                overflowY: "auto",
                padding: "20px",
              }}
            >
              <h2
                className="font_family_base"
                style={{
                  color: "#4CAF50",
                  marginBottom: 20,
                  textAlign: "center",
                }}
              >
                GAMES
              </h2>
              {games.map((game, index) => {
                const isPregame =
                  msFromStartTime(game.start_time) > Date.now();
                return (
                  <button
                    key={game.id}
                    onClick={() => navigateToGame(index)}
                    className="font_family_base"
                    style={{
                      width: "100%",
                      padding: "15px",
                      marginBottom: "10px",
                      backgroundColor:
                        currentGameId === game.id ? "#007bff" : "#181818",
                      color: "#fff",
                      border: "none",
                      borderRadius: 8,
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span>
                        {getIconAndColor(game.sport_id)} {game.away_team} vs{" "}
                        {game.home_team}
                      </span>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: "bold",
                          padding: "2px 6px",
                          borderRadius: 4,
                          backgroundColor: isPregame ? "#555" : "#c0392b",
                          color: "#fff",
                          marginLeft: 8,
                          flexShrink: 0,
                        }}
                      >
                        {isPregame ? "Pregame" : "Live"}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
